import classNames from 'classnames';

import { getMultiPartStatuses, getStatus, SUBMISSION_STATUS, SUBMISSION_STATUS_CONFIG } from 'my-core/quiz-utils';
import { removeExtraDecimals } from 'my-utils';

import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const rootCss = ({ palette, spacing }) => ({
  '&.QQR-result': {
    textAlign: 'right',
    '& > * + *': { display: 'inline-block', marginLeft: spacing(1) },
  },
  '&.QQR-primary': { color: palette.primary.main },
  '&.QQR-success': { color: palette.success.main },
  '&.QQR-error': { color: palette.error.main },
  '&.QQR-yellow': { color: palette.yellow.main },
});

export default function QuizQuestionResult({
  partIndex,
  question,
  showResults: showResultsProp,
  submission,
  weight: weightProp,
}) {
  const isMPQ = partIndex != null && question?.type === 'MultiPartQuestion';
  const subResult = isMPQ ? submission?.result?.breakdown?.[question.sub_questions[partIndex]?.id] : submission?.result;
  const showResults = showResultsProp || subResult?.total !== undefined;

  let weight = weightProp ?? getQuestionTotalScore(question) ?? 1;
  if (isMPQ) {
    weight /= question.sub_questions?.length || 1;
  }

  if (!showResults) {
    return (
      <Typography align="right" variant="body3">
        /
        {question ?
          removeExtraDecimals(weight)
        : <Skeleton sx={{ display: 'inline-block' }} variant="text" width={12} />}
      </Typography>
    );
  }

  const status = isMPQ ? getMultiPartStatuses(submission, question)[partIndex] : getStatus(submission, question);
  const { color, icon, label } =
    SUBMISSION_STATUS_CONFIG[status] || SUBMISSION_STATUS_CONFIG[SUBMISSION_STATUS.UNANSWERED];

  const score = subResult?.points ? subResult?.points / subResult?.total : 0;

  return (
    <div className={classNames(`QQR-${color}`, 'QQR-result')} css={rootCss}>
      {icon && <FontAwesomeIcon icon={icon} size="lg" />}
      <Typography
        sx={{
          color: 'inherit',
        }}
        variant="subtitle1"
      >
        {label}
      </Typography>
      <Typography variant="body3">{`${removeExtraDecimals(score * weight)}/${removeExtraDecimals(weight)}`}</Typography>
    </div>
  );
}

// QuizQuestionResult.propTypes = {
//   question: PropTypes.object,
//   showResults: PropTypes.bool,
//   submission: PropTypes.object,
//   weight: PropTypes.number,
// };

function getQuestionTotalScore(question) {
  if (!question) return;
  switch (question.type) {
    case 'TableInputs':
      return question.input_matrix?.reduce(
        (mem, row) => mem + row.reduce((mem, cell) => mem + (cell.type === 'input' ? 1 : 0), 0),
        0,
      );
    case 'MultipleShortAnswers':
      return question.inputs?.length;
    case 'FillInTheBlank':
      return Object.values(question.fitb_content?.entityMap || {}).filter(e => e.type === 'FILL_IN_THE_BLANK').length;
    case 'MixMatch':
      return question.pairings?.length;
    case 'AccountingJournalEntry':
      return question.transactions?.length * 2;
    case 'MultiPartQuestion':
      return question.sub_questions.reduce((mem, q) => mem + getQuestionTotalScore(q), 0);
    // case 'MarkYourself':
    // case 'MultipleChoice':
    // case 'ShortAnswer':
    default:
      return 1;
  }
}
